import { md5 } from "js-md5"

const DEVICE_ID_STORAGE_PREFIX = "chatred_device_id"

type BrowserInfo = {
  id: string
  name: string
}

function safeString(v: unknown) {
  return v == null ? "" : String(v)
}

function getUserAgent(): string {
  return typeof navigator !== "undefined" ? navigator.userAgent || "" : ""
}

/** 小米 / 红米 / POCO 自带浏览器（UA 常带 Chrome 内核标识，须优先于 Chrome 判断） */
function isMiBrowser(ua: string): boolean {
  return (
    /MiuiBrowser/i.test(ua) ||
    /XiaoMi\/MiuiBrowser/i.test(ua) ||
    /Xiaomi\/MiuiBrowser/i.test(ua) ||
    /XiaoMi\/MiBrowser/i.test(ua) ||
    /MiBrowser\//i.test(ua)
  )
}

function parseMiBrowserVersion(ua: string): string {
  const m = ua.match(/MiuiBrowser\/([\d.]+)/i) || ua.match(/MiBrowser\/([\d.]+)/i)
  if (!m?.[1]) return ""
  return m[1].split("-")[0]
}

function parseAndroidVersion(ua: string): string {
  const m = ua.match(/Android\s+([\d.]+)/i)
  return m?.[1] ?? ""
}

function parseIosVersion(ua: string): string {
  const m = ua.match(/OS\s+([\d_]+)\s+like\s+Mac/i)
  return m?.[1]?.replace(/_/g, ".") ?? ""
}

function parseAndroidDeviceModel(ua: string): string {
  const m = ua.match(/Android\s+[\d.]+;\s*(?:[^;)]*;\s*)*([^;)]+)\s+Build\//i)
  if (!m?.[1]) return ""
  const model = m[1].trim()
  if (/^(zh-cn|en-us|zh-tw|zh-hk|wv)$/i.test(model)) return ""
  if (/^U$/i.test(model)) return ""
  return model
}

function parseDeviceModel(ua: string): string {
  if (/iPhone/i.test(ua)) return "iPhone"
  if (/iPad/i.test(ua)) return "iPad"
  const androidModel = parseAndroidDeviceModel(ua)
  if (androidModel) return androidModel
  if (/HarmonyOS/i.test(ua) || /\bHMSCore\b/i.test(ua)) return "HarmonyOS Device"
  return ""
}

function parseDeviceBrand(ua: string, model: string): string {
  if (/iPhone|iPad|Macintosh/i.test(ua)) return "Apple"
  if (isMiBrowser(ua) || /\b(Xiaomi|XiaoMi|Redmi|POCO|MI\s)/i.test(ua) || /^24\d{8}/.test(model)) {
    return "Xiaomi"
  }
  if (/\bHuawei\b|HUAWEI|Honor|HONOR/i.test(ua)) return "Huawei"
  if (/\bOPPO\b|HeyTapBrowser/i.test(ua)) return "OPPO"
  if (/\bvivo\b/i.test(ua)) return "vivo"
  if (/\bSamsung\b|SM-/i.test(ua)) return "Samsung"
  return ""
}

function detectBrowser(): BrowserInfo {
  if (typeof navigator === "undefined") {
    return { id: "unknown", name: "Unknown Browser" }
  }

  const ua = getUserAgent()

  if (isMiBrowser(ua)) {
    const ver = parseMiBrowserVersion(ua)
    return { id: "mi", name: ver ? `Mi Browser ${ver}` : "Mi Browser" }
  }
  if (/\bHuaweiBrowser\/|\bHBPC\/|\bHUAWEI\b.*\bBrowser\b/i.test(ua)) {
    return { id: "huawei", name: "Huawei Browser" }
  }
  if (/\bHeyTapBrowser\//i.test(ua)) {
    return { id: "oppo", name: "HeyTap Browser" }
  }
  if (/\bVivoBrowser\//i.test(ua)) {
    return { id: "vivo", name: "vivo Browser" }
  }
  if (/\bSamsungBrowser\//i.test(ua)) {
    return { id: "samsung", name: "Samsung Internet" }
  }
  if (/\bUCBrowser\//i.test(ua)) {
    return { id: "uc", name: "UC Browser" }
  }
  if (/\bMQQBrowser\//i.test(ua)) {
    return { id: "qq", name: "QQ Browser" }
  }
  if (/\bMicroMessenger\//i.test(ua)) {
    return { id: "wechat", name: "WeChat WebView" }
  }
  if (/\bEdg\//.test(ua) || /\bEdgiOS\//.test(ua)) {
    return { id: "edge", name: "Microsoft Edge" }
  }
  if (/\bOPR\/|Opera/.test(ua) || /\bOPiOS\//.test(ua)) {
    return { id: "opera", name: "Opera" }
  }
  if (/\bFirefox\//.test(ua) || /\bFxiOS\//.test(ua)) {
    return { id: "firefox", name: "Firefox" }
  }
  if (/\bChrome\//.test(ua) || /\bCriOS\//.test(ua)) {
    return { id: "chrome", name: "Google Chrome" }
  }
  if (
    /\bSafari\//.test(ua) &&
    !/\bChrome\//.test(ua) &&
    !/\bCriOS\//.test(ua) &&
    !/\bEdgiOS\//.test(ua) &&
    !/\bFxiOS\//.test(ua) &&
    !/\bOPiOS\//.test(ua)
  ) {
    return { id: "safari", name: "Safari" }
  }
  if (/\bMSIE\b|Trident\//.test(ua)) {
    return { id: "ie", name: "Internet Explorer" }
  }

  return { id: "browser", name: "Web Browser" }
}

function getOsFamily(): string {
  const ua = getUserAgent()
  if (/Android/i.test(ua)) return "Android"
  if (/iPhone|iPad|iPod/i.test(ua)) return "iOS"
  if (/Windows/i.test(ua)) return "Windows"
  if (/Macintosh|Mac OS X/i.test(ua)) return "macOS"
  if (/Linux/i.test(ua)) return "Linux"
  return "Web"
}

function getOsVersionLabel(): string {
  const ua = getUserAgent()
  const family = getOsFamily()
  if (family === "Android") {
    const v = parseAndroidVersion(ua)
    return v ? `Android ${v}` : "Android"
  }
  if (family === "iOS") {
    const v = parseIosVersion(ua)
    return v ? `iOS ${v}` : "iOS"
  }
  return family
}

/** 登录设备列表 / X-Device-Name 使用的可读名称 */
export function getWebLoginDeviceName(): string {
  const ua = getUserAgent()
  const browser = detectBrowser()
  const model = parseDeviceModel(ua)
  const brand = parseDeviceBrand(ua, model)
  const os = getOsVersionLabel()

  const deviceLabel =
    model && brand && !model.toLowerCase().includes(brand.toLowerCase())
      ? `${brand} ${model}`
      : model || brand

  return [deviceLabel, browser.name, os].filter(Boolean).join(" · ") || browser.name
}

/** 不用随机 id：用浏览器标识 + 设备指纹生成稳定 id */
function buildWebDeviceFingerprint(browserId: string): string {
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return md5(`${browserId}|unknown`)
  }
  const ua = safeString(navigator.userAgent)
  const lang = safeString(navigator.language)
  const platform = safeString((navigator as any).platform)
  const tz = safeString(new Date().getTimezoneOffset())
  const screenSize =
    typeof window.screen !== "undefined"
      ? `${safeString(window.screen.width)}x${safeString(window.screen.height)}@${safeString(window.devicePixelRatio)}`
      : ""
  return md5([browserId, ua, lang, platform, tz, screenSize].join("|"))
}

export function getWebDeviceId(): string {
  const browser = detectBrowser()
  const fingerprint = buildWebDeviceFingerprint(browser.id)
  const id = `${browser.id}-${fingerprint}`

  if (typeof localStorage !== "undefined") {
    const storageKey = `${DEVICE_ID_STORAGE_PREFIX}_${browser.id}`
    const existing = localStorage.getItem(storageKey)
    if (existing) return existing
    localStorage.setItem(storageKey, id)
  }

  return id
}

export function getWebBrowserName(): string {
  return detectBrowser().name
}

export function getWebBrowserId(): string {
  return detectBrowser().id
}

export default {
  getUniqueId: async () => getWebDeviceId(),
  getDeviceName: async () => getWebLoginDeviceName(),
  getLoginDeviceName: async () => getWebLoginDeviceName(),
  getVersion: () => "1.0.0",
  getBuildNumber: () => "1",
  getModel: () => parseDeviceModel(getUserAgent()) || detectBrowser().id,
  getBrand: () => parseDeviceBrand(getUserAgent(), parseDeviceModel(getUserAgent())),
  getSystemName: () => getOsFamily(),
  getSystemVersion: () => getOsVersionLabel(),
}
